import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { KeycloakAuthService } from "src/app/services/keycloak-auth.service";
import {
  analyzeTripWsPath,
  resolveAnalyzeTripWsUrl,
  shouldPassKcAuthInWsQuery,
} from "src/app/config/analyze-trip-ws.config";

export interface WsEnvelope<T = unknown> {
  type: string;
  v: number;
  payload?: T;
}

export interface AnalyzeReportUpdatedWsPayload {
  profileId?: number;
  uuid?: string;
  event?: string;
  statusReportAnalyze?: string;
  statusAnalyze?: string;
  timestamp?: string;
}

@Injectable({
  providedIn: "root",
})
export class AnalyzeReportOperatorWsService {
  private socket: WebSocket | null = null;
  private profileId: string | null = null;
  private shouldReconnect = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reconnectDelayMs = 5000;

  private updatesSubject = new Subject<AnalyzeReportUpdatedWsPayload>();
  public updates$ = this.updatesSubject.asObservable();

  constructor(private keycloakAuthService: KeycloakAuthService) {}

  public connect(profileId: string): void {
    if (!profileId) {
      return;
    }

    this.profileId = profileId;
    this.shouldReconnect = true;
    this.keycloakAuthService.updateToken(30).finally(() => this.openSocket());
  }

  public disconnect(): void {
    this.shouldReconnect = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.onclose = null;
      this.socket.close();
      this.socket = null;
    }
  }

  private openSocket(): void {
    if (!this.profileId) {
      return;
    }

    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const token = this.keycloakAuthService.getToken();
    const params = new URLSearchParams({ profileId: this.profileId });

    // Local direto :8081 — backend aceita kc-auth na query (browser nao envia header)
    if (token && shouldPassKcAuthInWsQuery()) {
      params.set("kc-auth", `Bearer ${token}`);
    }

    const url = `${resolveAnalyzeTripWsUrl()}?${params.toString()}`;

    // Deploy: cookie setera_kc_access_token (nginx → kc-auth). Local: query kc-auth.
    // Nao usar Sec-WebSocket-Protocol em deploy — nginx nao ecoa o header e o handshake falha.
    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      console.log("WebSocket analyze-report operator conectado", {
        path: analyzeTripWsPath,
      });
    };

    this.socket.onmessage = (event: MessageEvent) => {
      try {
        const envelope = JSON.parse(event.data) as WsEnvelope;
        if (!envelope?.type) {
          return;
        }

        switch (envelope.type) {
          case "connection.ready":
            console.debug("WS analyze-report connection.ready", envelope.payload);
            break;
          case "analyze.report.updated":
            if (envelope.payload) {
              this.updatesSubject.next(
                envelope.payload as AnalyzeReportUpdatedWsPayload
              );
            }
            break;
          case "ping":
            this.sendPong();
            break;
          default:
            break;
        }
      } catch (error) {
        console.debug("Mensagem WS analyze-report ignorada", error);
      }
    };

    this.socket.onerror = () => {
      console.warn("Erro na conexao WebSocket analyze-report operator");
    };

    this.socket.onclose = () => {
      this.socket = null;
      if (this.shouldReconnect) {
        this.reconnectTimer = setTimeout(() => {
          this.keycloakAuthService
            .updateToken(30)
            .finally(() => this.openSocket());
        }, this.reconnectDelayMs);
      }
    };
  }

  private sendPong(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    this.socket.send(JSON.stringify({ type: "pong", v: 1 }));
  }
}
